/* FILE: ./frontend/src/features/admin/SeoOrchestrator.jsx */
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

// The "Iron Gate" reminder for the Admin
const ALLOWED_CHAPTERS = ['25', '32', '39', '44', '68', '69', '70', '72', '73', '74', '76', '83'];

export default function SeoOrchestrator() {
  const [inputText, setInputText] = useState('');
  const [stagedKeywords, setStagedKeywords] = useState([]);
  const [existingSynonyms, setExistingSynonyms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState([]);

  useEffect(() => {
    fetchExisting();
  }, []);

  const fetchExisting = async () => {
    const { data } = await supabase.from('search_synonyms').select('alias_keyword');
    if (data) setExistingSynonyms(data.map(d => d.alias_keyword.toLowerCase()));
  };

  const handleIntake = () => {
    if (!inputText.trim()) return;
    
    const rawLines = inputText.split('\n').map(line => line.trim()).filter(Boolean);
    
    const parsed = rawLines.map(line => {
      let phrase = line;
      let chapter = '';
      
      const lastDashIndex = line.lastIndexOf('-');
      
      if (lastDashIndex !== -1) {
          phrase = line.substring(0, lastDashIndex).trim();
          chapter = line.substring(lastDashIndex + 1).trim().replace(/\D/g, ''); 
      }

      const isExisting = existingSynonyms.includes(phrase.toLowerCase());
      const isValidChapter = ALLOWED_CHAPTERS.includes(chapter);

      return {
        phrase,
        chapter: isValidChapter ? chapter : '',
        isNew: !isExisting,
        status: isExisting ? 'Duplicate' : (isValidChapter ? 'Ready' : 'Missing Chapter')
      };
    });

    setStagedKeywords(parsed);
    setSelectedIndices([]); // Reset selections on new intake
  };

  const toggleSelect = (index) => {
    if (selectedIndices.includes(index)) {
      setSelectedIndices(selectedIndices.filter(i => i !== index));
    } else {
      setSelectedIndices([...selectedIndices, index]);
    }
  };

  // ⚡ NEW: Publish a single row instantly
  const handlePublishSingle = async (index, e) => {
    e.stopPropagation(); // Prevent the row click from checking the box
    
    const item = stagedKeywords[index];
    if (!item.chapter) return alert("Please assign a valid HS Chapter first.");

    const slug = item.phrase.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

    setLoading(true);
    try {
        const { data: pageData, error: pageError } = await supabase
            .from('seo_landing_pages')
            .insert([{
                master_keyword: item.phrase,
                slug: slug,
                hs_chapter: item.chapter,
                status: 'published'
            }])
            .select()
            .single();

        if (pageError) throw pageError;

        const { error: synError } = await supabase.from('search_synonyms').insert([{
            landing_page_id: pageData.id,
            alias_keyword: item.phrase,
            language: 'en'
        }]);
        if (synError) throw synError;

        // Remove published item from staging
        setStagedKeywords(prev => prev.filter((_, i) => i !== index));
        setSelectedIndices([]); 
        fetchExisting();

    } catch (err) {
        console.error("Publishing Error:", err);
        alert(`Error: ${err.message}`);
    } finally {
        setLoading(false);
    }
  };

  // 2. THE CLUBBING: Merge selected phrases into one Master Page
  const handleClubbing = async () => {
    if (selectedIndices.length < 1) return alert("Select at least one phrase to process.");
    
    const masterItem = stagedKeywords[selectedIndices[0]];
    if (!masterItem.chapter) return alert(`Master item "${masterItem.phrase}" must have an allowed HS Chapter assigned.`);

    const aliases = selectedIndices.map(i => stagedKeywords[i].phrase);
    
    const masterKeyword = prompt("Confirm the Master Keyword for the URL Slug:", masterItem.phrase);
    if (!masterKeyword) return;

    const slug = masterKeyword.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

    setLoading(true);
    try {
        const { data: pageData, error: pageError } = await supabase
            .from('seo_landing_pages')
            .insert([{
                master_keyword: masterKeyword,
                slug: slug,
                hs_chapter: masterItem.chapter,
                status: 'published'
            }])
            .select()
            .single();

        if (pageError) throw pageError;

        const synonymPayload = aliases.map(alias => ({
            landing_page_id: pageData.id,
            alias_keyword: alias,
            language: 'en'
        }));

        const { error: synError } = await supabase.from('search_synonyms').insert(synonymPayload);
        if (synError) throw synError;

        alert(`✅ Processed successfully!\nCreated Master Page: /materials/${slug}\nClubbed ${aliases.length} aliases.`);
        
        setStagedKeywords(prev => prev.filter((_, idx) => !selectedIndices.includes(idx)));
        setSelectedIndices([]);
        fetchExisting();

    } catch (err) {
        console.error("Processing Error:", err);
        alert(`Error: ${err.message}`);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-6 border-b border-gray-100 pb-4">
        <h2 className="text-2xl font-black text-gray-900">SEO Orchestrator</h2>
        <p className="text-sm text-gray-500 mt-1">Paste keywords generated from your Gemini queries to build Programmatic Landing Pages.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT COL: INTAKE */}
        <div className="lg:col-span-1 space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Paste Top Phrases (1 per line)</label>
            <p className="text-xs text-gray-500 mb-2">Format: `Phrase - Chapter` (e.g., `Structural Steel H-Beams - 72`)</p>
            <textarea
              className="w-full h-64 p-3 border border-gray-300 rounded-lg text-sm font-mono focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              placeholder="Bulk W-Beam pricing - 72&#10;Ready mix concrete delivery - 25&#10;SPF lumber wholesale - 44"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
          </div>
          <button 
            onClick={handleIntake}
            className="w-full bg-slate-800 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-700"
          >
            Analyze & Diff Keywords
          </button>
        </div>

        {/* RIGHT COL: STAGING & CLUBBING */}
        <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">Staged for Processing</h3>
                <button 
                    onClick={handleClubbing}
                    disabled={selectedIndices.length <= 1 || loading}
                    className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg shadow-sm hover:bg-blue-700 disabled:opacity-50"
                    title="Select 2 or more phrases to club them together."
                >
                    {loading ? 'Processing...' : `Club Bulk (${selectedIndices.length})`}
                </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden h-96 overflow-y-auto shadow-inner">
                {stagedKeywords.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm font-medium">
                        Awaiting input analysis...
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-slate-50 sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Select</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Phrase</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Chapter</th>
                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {stagedKeywords.map((item, idx) => (
                                <tr 
                                    key={idx} 
                                    className={`cursor-pointer transition-colors ${selectedIndices.includes(idx) ? 'bg-blue-50' : 'hover:bg-gray-50'} ${!item.isNew ? 'opacity-50' : ''}`}
                                    onClick={() => item.isNew && toggleSelect(idx)}
                                >
                                    <td className="px-4 py-3">
                                        <input 
                                            type="checkbox" 
                                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                            checked={selectedIndices.includes(idx)}
                                            onChange={() => {}} 
                                            disabled={!item.isNew}
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                        {item.phrase}
                                        {!item.isNew && <span className="ml-2 text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-bold uppercase">Duplicate</span>}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {item.chapter ? (
                                             <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono text-xs border border-gray-200">{item.chapter}</span>
                                        ) : (
                                            <input 
                                                type="text" 
                                                placeholder="HS?" 
                                                className="w-16 p-1 text-xs border rounded"
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                    const updated = [...stagedKeywords];
                                                    updated[idx].chapter = e.target.value;
                                                    setStagedKeywords(updated);
                                                }}
                                            />
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {item.isNew && item.status !== 'Missing Chapter' && (
                                            <button 
                                                onClick={(e) => handlePublishSingle(idx, e)}
                                                disabled={loading}
                                                className="text-xs font-bold bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded hover:bg-green-100 transition-colors whitespace-nowrap"
                                            >
                                                Publish Solo
                                            </button>
                                        )}
                                        {item.status === 'Missing Chapter' && (
                                            <span className="text-xs text-amber-600 font-bold px-2">Needs HS</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
      </div>
    </div>
  );
}